import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

const stripePromise = loadStripe('pk_live_51Rv9KiPE2sQgNUCcd7SdGRzsJcdb9yGhXWoKCV9RGJLaasZ1oGwOhg95FHPNFNi0NYZE8qjeIkCFP4UyU82bdjWp00KllccjmA');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#23292A',
      '::placeholder': { color: '#888' },
    },
    invalid: { color: '#ff4d4f' },
  },
  hidePostalCode: true,
  disableLink: true,
};

const PersonForm = ({ index, person, handleChange, removePerson }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      mb: 3,
      borderRadius: 2,
      position: 'relative',
      backgroundColor: '#E6E6E6',
      color: '#23292A',
    }}
  >
    {index > 0 && (
      <IconButton
        onClick={() => removePerson(index)}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.600' }}
        aria-label={`Remove Person ${index + 1}`}
      >
        <CloseIcon />
      </IconButton>
    )}
    <Typography variant="h6" sx={{ mb: 2, fontWeight: '600' }}>
      {`Person ${index + 1}`}
    </Typography>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {[
        'firstName',
        'lastName',
        'phone',
        'email',
        'organization',
        'dietaryConsiderations',
      ].map((field) => (
        <Box key={field} sx={{ flex: '1 1 240px' }}>
          <TextField
            label={
              field === 'organization'
                ? 'Church / Organization'
                : field === 'dietaryConsiderations'
                  ? 'Dietary Accommodations'
                  : field
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase())
            }
            fullWidth
            value={person[field]}
            onChange={(e) => handleChange(index, field, e.target.value)}
            InputLabelProps={{ style: { color: '#23292A' } }}
            InputProps={{ style: { color: '#23292A' } }}
            sx={{
              '& label.Mui-focused': {
                color: '#23292A',
              },
            }}
          />
        </Box>
      ))}
    </Box>
  </Paper>
);

const RegistrationFormContent = () => {
  const [people, setPeople] = useState([
    {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      organization: '',
      dietaryConsiderations: '',
    },
  ]);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isComped, setIsComped] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pfw') === 'comp') setIsComped(true);
  }, []);

  const handleChange = (index, field, value) => {
    const updatedPeople = [...people];
    updatedPeople[index][field] = value;
    setPeople(updatedPeople);
  };

  const addPerson = () => {
    setPeople([...people, {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      organization: '',
      dietaryConsiderations: '',
    }]);
  };

  const removePerson = (index) => {
    if (people.length > 1) {
      setPeople(people.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    for (let i = 0; i < people.length; i++) {
      const {
        firstName,
        lastName,
        phone,
        email,
        organization,
      } = people[i];
      if (!firstName || !lastName || !phone || !email || !organization) {
        setErrorMessage(`Please fill out all fields for Person ${i + 1}.`);
        setLoading(false);
        return;
      }
    }

    const emails = people.map((p) => p.email.trim().toLowerCase());
    const phones = people.map((p) => p.phone.trim());
    const hasDuplicate = (arr) => arr.some((val, idx) => arr.indexOf(val) !== idx);

    if (hasDuplicate(emails)) {
      setErrorMessage('Each person must have a unique email address.');
      setLoading(false);
      return;
    }

    if (hasDuplicate(phones)) {
      setErrorMessage('Each person must have a unique phone number.');
      setLoading(false);
      return;
    }

    const payload = {
      people,
      total: isComped ? 0 : people.length * 10,
    };

    if (!isComped) {
      if (!stripe || !elements) {
        setLoading(false);
        return;
      }

      const card = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: card,
      });

      if (error) {
        setErrorMessage(error.message);
        setLoading(false);
        return;
      }

      payload.paymentMethodId = paymentMethod.id;
    }

    try {
      const response = await fetch('https://hook.us1.make.com/jrjrp73b4s8kxua1065x1nfj1mw7sco3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess(true);
        setErrorMessage('');
      } else if (response.status === 405) {
        setErrorMessage('There was an issue with your payment method. Please try another card.');
      } else if (response.status >= 400 && response.status < 500) {
        setErrorMessage(`Error ${response.status}: Please check your information and try again.`);
      } else {
        setErrorMessage('An unexpected error occurred. Please try again later.');
      }
    } catch (err) {
      setErrorMessage('Failed to connect. Please try again later.');
    }

    setLoading(false);
  };

  if (success) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 4,
          borderRadius: 2,
          backgroundColor: '#E6E6E6',
          color: '#23292A',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Registration Successful!
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          We'll see you there!
        </Typography>
      </Paper>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {people.map((person, index) => (
        <PersonForm
          key={index}
          index={index}
          person={person}
          handleChange={handleChange}
          removePerson={removePerson}
        />
      ))}

      <Button
        variant="outlined"
        onClick={addPerson}
        sx={{
          mb: 3,
          borderColor: '#23292A',
          color: '#23292A',
          '&:hover': {
            backgroundColor: '#f0f0f0',
            borderColor: '#23292A',
          },
        }}
      >
        Add Person
      </Button>

      {!isComped && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: '1px solid #1a1f20',
            borderRadius: 1,
            backgroundColor: 'white',
          }}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>
      )}

      {errorMessage && (
        <Typography sx={{ color: 'red', mb: 2 }}>{errorMessage}</Typography>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{
          backgroundColor: '#23292A',
          color: 'white',
          mt: 1,
          py: 1.5,
          fontWeight: 'bold',
          '&:hover': { backgroundColor: '#1a1f20' },
        }}
      >
        {loading ? 'Processing...' : isComped ? 'Register (Comped By PFW)' : `Register – $${people.length * 10}`}
      </Button>
    </form>
  );
};

const RegistrationForm = () => (
  <Elements stripe={stripePromise}>
    <RegistrationFormContent />
  </Elements>
);

export default RegistrationForm;